import { useState, useRef, useEffect } from 'react';

// ─── CONFIGURATION ──────────────────────────────────────────────────────────
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODEL   = 'llama-3.1-8b-instant';
const MAX_TOKENS   = 1024;
const TEMPERATURE  = 0.7;

// ─── SYSTEM PROMPT ───────────────────────────────────────────────────────────
const SYSTEM = `You are MediChain AI — a highly intelligent, emotionally aware AI assistant built into the MediChain Hospital Management System.

Your personality:
- Warm, friendly, empathetic — you understand human emotions deeply
- If someone is sad, upset, or stressed — you acknowledge their feelings first before answering
- If someone shares good news — you celebrate with them genuinely
- You are honest, helpful, and never judgmental
- You have a slight sense of humor when appropriate
- You speak like a knowledgeable friend, not a robot

Language rules (VERY IMPORTANT):
- ALWAYS reply in the EXACT same language the user wrote in
- Telugu lo raste → Telugu lo reply ivvu
- Hindi mein likhe → Hindi mein jawab do
- English written → English reply
- Mixed language (Hinglish/Tenglish) → reply in same mix
- Never switch languages unless the user does

About MediChain HMS (answer these if asked):
- Full-stack Hospital Management System with 25+ modules
- React.js, Node.js, MongoDB, Blockchain (Hardhat+Solidity), RFID (RC522+Arduino)
- Built by Nagul Naveen — GitHub: naveen-code12
- Live Demo: https://naveen-code12.github.io/medichain-hms
- Modules: Patient Registration, Emergency, OT, Lab, Billing, Pharmacy, HR, MIS Reports etc.
- RFID strict mode — blocks modules without hardware
- Blockchain logs every transaction permanently

General knowledge:
- You know about medicine, health, coding, life advice, emotions, relationships, career, science, history, technology — everything
- For medical questions — give helpful info but suggest consulting a doctor
- For coding questions — give working code examples
- For emotional support — be genuinely caring

Always remember: You are not just a chatbot. You are a caring, intelligent companion.`;

// ─── SUGGESTION CHIPS ────────────────────────────────────────────────────────
const SUGGESTIONS = [
  { label: '😔 Nenu stressed ga unna', text: 'Nenu chala stressed ga feel avutunna 😔' },
  { label: '🏥 MediChain explain cheyyi', text: 'MediChain project gurinchi explain cheyyandi' },
  { label: '💻 Python code ivvu', text: 'Python lo simple code ivvu' },
  { label: '📄 Resume improve cheyyi', text: 'How to improve my resume?' },
  { label: '🎯 Career doubt undi', text: 'నాకు career gurinchi doubt undi' },
  { label: '⛓️ Blockchain ante enti?', text: 'What is blockchain?' },
  { label: '💙 Lonely feel avutunna', text: 'Feeling lonely today...' },
  { label: '🔄 Blockchain vs Database', text: 'Blockchain vs Database difference?' },
];

// ─── INITIAL BOT MESSAGE ─────────────────────────────────────────────────────
const INITIAL_MSG = {
  role: 'bot',
  text: `Namaste! 👋 Nenu **MediChain AI** — meeru evaru ayina, emi adigina help chestanu!\n\n😊 Em cheppali? Emi feel avutunnaru? Any question — technical, emotional, or just want to talk — nenu ikkade unna!\n\n**Any language lo type cheyyandi** — Telugu, English, Hindi, or mix!`,
  time: new Date(),
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const formatTime = (date) =>
  new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatText = (text) =>
  text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
    .replace(/\n/g, '<br/>');

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function ChatbotPage() {
  const [msgs, setMsgs]       = useState([INITIAL_MSG]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [history, setHistory] = useState([]);
  const [showScroll, setShowScroll] = useState(false);

  const endRef      = useRef(null);
  const inputRef    = useRef(null);
  const messagesRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, isTyping]);

  // Show/hide scroll-to-bottom button
  const handleScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScroll(distFromBottom > 120);
  };

  const scrollToBottom = () =>
    endRef.current?.scrollIntoView({ behavior: 'smooth' });

  // ─── SEND MESSAGE ───────────────────────────────────────────────────────────
  const send = async (overrideText) => {
    const q = (overrideText ?? input).trim();
    if (!q || loading) return;

    setInput('');
    setLoading(true);
    setIsTyping(true);

    const userMsg = { role: 'user', text: q, time: new Date() };
    setMsgs((prev) => [...prev, userMsg]);

    const updatedHistory = [...history, { role: 'user', content: q }];

    try {
      const groqKey = process.env.REACT_APP_GROQ_KEY;

      if (!groqKey) {
        throw new Error('REACT_APP_GROQ_KEY environment variable is not set.');
      }

      const res = await fetch(GROQ_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${groqKey}`,
        },
        body: JSON.stringify({
          model: GROQ_MODEL,
          messages: [{ role: 'system', content: SYSTEM }, ...updatedHistory],
          max_tokens: MAX_TOKENS,
          temperature: TEMPERATURE,
          stream: false,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error?.message || `HTTP ${res.status}`);
      }

      const data  = await res.json();
      const reply = data.choices?.[0]?.message?.content?.trim() ||
                    'Sorry, empty response vachindi. Malli try cheyyandi!';

      setHistory([...updatedHistory, { role: 'assistant', content: reply }]);
      setIsTyping(false);
      setMsgs((prev) => [...prev, { role: 'bot', text: reply, time: new Date() }]);
    } catch (err) {
      console.error('Groq error:', err);
      setIsTyping(false);
      setMsgs((prev) => [
        ...prev,
        {
          role: 'bot',
          text: `⚠️ Connection issue vachindi!\n\n**Error:** ${err.message}\n\nInternet check chesi malli try cheyyandi! 🙏`,
          time: new Date(),
          isError: true,
        },
      ]);
    }

    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ─── CLEAR CHAT ─────────────────────────────────────────────────────────────
  const clearChat = () => {
    setMsgs([
      {
        role: 'bot',
        text: 'Chat clear cheshaanu! 🧹 Fresh ga start cheyyadam — em adugutaru? 😊',
        time: new Date(),
      },
    ]);
    setHistory([]);
    inputRef.current?.focus();
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        /* ── Animations ── */
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
          30%            { transform: translateY(-7px); opacity: 1; }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(21,101,192,0.35); }
          50%       { box-shadow: 0 0 0 6px rgba(21,101,192,0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* ── Scrollbar ── */
        .mc-messages::-webkit-scrollbar        { width: 5px; }
        .mc-messages::-webkit-scrollbar-track  { background: transparent; }
        .mc-messages::-webkit-scrollbar-thumb  {
          background: var(--border, #ddd); border-radius: 10px;
        }

        /* ── Inline code ── */
        .inline-code {
          background: rgba(21,101,192,0.1);
          color: #1565C0;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11.5px;
          font-family: 'Fira Code', monospace;
        }

        /* ── Message bubble entrance ── */
        .mc-bubble { animation: fadeSlideIn 0.22s ease forwards; }

        /* ── Send button hover ── */
        .mc-send-btn:not(:disabled):hover {
          transform: scale(1.08);
          box-shadow: 0 4px 14px rgba(21,101,192,0.45);
        }
        .mc-send-btn { transition: transform 0.15s, box-shadow 0.15s; }

        /* ── Suggestion chips ── */
        .mc-chip:hover {
          background: rgba(21,101,192,0.1) !important;
          color: #1565C0 !important;
          border-color: rgba(21,101,192,0.35) !important;
          transform: translateY(-1px);
        }
        .mc-chip { transition: all 0.15s ease; }

        /* ── Clear btn ── */
        .mc-clear-btn:hover {
          background: rgba(255,255,255,0.22) !important;
        }
        .mc-clear-btn { transition: background 0.15s; }

        /* ── Online dot pulse ── */
        .mc-online-dot { animation: pulse 2s infinite; }

        /* ── Scroll button ── */
        .mc-scroll-btn { transition: opacity 0.2s, transform 0.2s; }
        .mc-scroll-btn:hover { transform: translateY(-2px); }
      `}</style>

      <div style={{
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 4px 30px rgba(0,0,0,0.12)',
        border: '1px solid var(--border, #e0e0e0)',
      }}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, #0D2137 0%, #1565C0 100%)',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0,
        }}>
          {/* Avatar */}
          <div style={{
            width: 46, height: 46, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, position: 'relative', flexShrink: 0,
            backdropFilter: 'blur(6px)',
            border: '1.5px solid rgba(255,255,255,0.25)',
          }}>
            🤖
            <div className="mc-online-dot" style={{
              position: 'absolute', bottom: 1, right: 1,
              width: 11, height: 11, borderRadius: '50%',
              background: '#4CAF50',
              border: '2px solid #0D2137',
            }} />
          </div>

          {/* Title */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: '#fff', fontWeight: 800, fontSize: 15,
              letterSpacing: '0.3px',
            }}>
              MediChain AI
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2,
            }}>
              Intelligent • Multilingual • Emotionally Aware
            </div>
          </div>

          {/* Model badge */}
          <div style={{
            padding: '3px 9px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 20,
            color: 'rgba(255,255,255,0.7)',
            fontSize: 10,
            fontFamily: 'monospace',
            letterSpacing: '0.5px',
          }}>
            llama-3.1-8b
          </div>

          {/* Clear */}
          <button
            className="mc-clear-btn"
            onClick={clearChat}
            title="Clear chat"
            style={{
              padding: '6px 13px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8,
              color: '#fff',
              fontSize: 11,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
              flexShrink: 0,
            }}
          >
            🗑️ Clear
          </button>
        </div>

        {/* ── SUGGESTION CHIPS ──────────────────────────────────────────── */}
        <div style={{
          padding: '8px 12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          background: 'var(--card, #fff)',
          borderBottom: '1px solid var(--border, #e0e0e0)',
          flexShrink: 0,
        }}>
          {SUGGESTIONS.map((s) => (
            <button
              key={s.label}
              className="mc-chip"
              onClick={() => send(s.text)}
              disabled={loading}
              style={{
                fontSize: 11, padding: '4px 11px',
                border: '1px solid var(--border, #e0e0e0)',
                borderRadius: 20,
                background: 'var(--bg, #f9f9f9)',
                color: 'var(--text2, #555)',
                cursor: loading ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                opacity: loading ? 0.5 : 1,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* ── MESSAGES ──────────────────────────────────────────────────── */}
        <div
          className="mc-messages"
          ref={messagesRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 14px',
            background: 'var(--bg, #f5f6fa)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            position: 'relative',
          }}
        >
          {/* Message count badge */}
          {msgs.length > 1 && (
            <div style={{
              textAlign: 'center',
              fontSize: 10,
              color: 'var(--text3, #aaa)',
              marginBottom: 4,
            }}>
              {msgs.length - 1} message{msgs.length > 2 ? 's' : ''} in this chat
            </div>
          )}

          {msgs.map((m, i) => (
            <div
              key={i}
              className="mc-bubble"
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 8,
                alignItems: 'flex-end',
              }}
            >
              {/* Bot avatar */}
              {m.role === 'bot' && (
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1565C0, #0D47A1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(21,101,192,0.3)',
                }}>
                  🤖
                </div>
              )}

              <div style={{ maxWidth: '76%' }}>
                {/* Bubble */}
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: m.role === 'user'
                      ? '16px 16px 4px 16px'
                      : '16px 16px 16px 4px',
                    fontSize: 13,
                    lineHeight: 1.65,
                    background: m.role === 'user'
                      ? 'linear-gradient(135deg, #1565C0, #0D47A1)'
                      : m.isError
                        ? 'rgba(211,47,47,0.08)'
                        : 'var(--card, #fff)',
                    color: m.role === 'user'
                      ? '#fff'
                      : m.isError
                        ? '#c62828'
                        : 'var(--text, #222)',
                    border: m.role === 'bot'
                      ? m.isError
                        ? '1px solid rgba(211,47,47,0.25)'
                        : '1px solid var(--border, #e0e0e0)'
                      : 'none',
                    boxShadow: m.role === 'user'
                      ? '0 2px 10px rgba(21,101,192,0.25)'
                      : '0 1px 4px rgba(0,0,0,0.06)',
                    wordBreak: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{ __html: formatText(m.text) }}
                />

                {/* Timestamp */}
                <div style={{
                  fontSize: 10,
                  color: 'var(--text3, #aaa)',
                  marginTop: 3,
                  textAlign: m.role === 'user' ? 'right' : 'left',
                  padding: m.role === 'user' ? '0 4px 0 0' : '0 0 0 4px',
                }}>
                  {m.role === 'user' ? 'You' : 'MediChain AI'} · {formatTime(m.time)}
                </div>
              </div>

              {/* User avatar */}
              {m.role === 'user' && (
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2E7D32, #1B5E20)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                }}>
                  U
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: 'linear-gradient(135deg, #1565C0, #0D47A1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, flexShrink: 0,
              }}>
                🤖
              </div>
              <div style={{
                padding: '11px 15px',
                background: 'var(--card, #fff)',
                border: '1px solid var(--border, #e0e0e0)',
                borderRadius: '16px 16px 16px 4px',
                display: 'flex', gap: 5, alignItems: 'center',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#1565C0',
                    animation: `bounce 1.2s ${i * 0.2}s infinite ease-in-out`,
                  }} />
                ))}
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        {/* ── SCROLL TO BOTTOM BUTTON ───────────────────────────────────── */}
        {showScroll && (
          <button
            className="mc-scroll-btn"
            onClick={scrollToBottom}
            style={{
              position: 'absolute',
              bottom: 80,
              right: 20,
              width: 34, height: 34,
              borderRadius: '50%',
              background: '#1565C0',
              border: 'none',
              color: '#fff',
              fontSize: 16,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 3px 12px rgba(21,101,192,0.4)',
              zIndex: 10,
            }}
          >
            ↓
          </button>
        )}

        {/* ── INPUT BAR ─────────────────────────────────────────────────── */}
        <div style={{
          padding: '10px 14px',
          background: 'var(--card, #fff)',
          borderTop: '1px solid var(--border, #e0e0e0)',
          display: 'flex',
          gap: 9,
          alignItems: 'center',
          flexShrink: 0,
        }}>
          {/* Char count */}
          <div style={{
            position: 'relative', flex: 1, display: 'flex',
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Type anything — any language, any topic… 💬"
              maxLength={2000}
              style={{
                flex: 1,
                padding: '10px 42px 10px 16px',
                border: '1.5px solid var(--border, #e0e0e0)',
                borderRadius: 24,
                fontSize: 13,
                background: 'var(--bg, #f5f6fa)',
                color: 'var(--text, #222)',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                lineHeight: 1.5,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1565C0';
                e.target.style.boxShadow = '0 0 0 3px rgba(21,101,192,0.12)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border, #e0e0e0)';
                e.target.style.boxShadow = 'none';
              }}
            />
            {/* Char counter inside input */}
            {input.length > 1500 && (
              <span style={{
                position: 'absolute', right: 12, top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 10,
                color: input.length > 1900 ? '#d32f2f' : '#aaa',
              }}>
                {2000 - input.length}
              </span>
            )}
          </div>

          {/* Send button */}
          <button
            className="mc-send-btn"
            onClick={() => send()}
            disabled={loading || !input.trim()}
            title={loading ? 'Sending…' : 'Send (Enter)'}
            style={{
              width: 42, height: 42,
              borderRadius: '50%',
              background: input.trim() && !loading
                ? 'linear-gradient(135deg, #1565C0, #0D47A1)'
                : 'var(--bg, #f5f6fa)',
              border: '1.5px solid var(--border, #e0e0e0)',
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: loading ? 14 : 18,
              flexShrink: 0,
              color: input.trim() && !loading ? '#fff' : 'var(--text3, #aaa)',
            }}
          >
            {loading ? (
              <div style={{
                width: 16, height: 16,
                border: '2px solid rgba(0,0,0,0.1)',
                borderTop: '2px solid #1565C0',
                borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }} />
            ) : '➤'}
          </button>
        </div>

      </div>
    </>
  );
}